import React, { useEffect, useState, useCallback } from 'react';
import { range12 } from '../lib/timeFormat';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { GraduationCap, MapPin, Video, Clock, User, Star, MessageSquarePlus, CheckCircle2 } from 'lucide-react';
import { indStatusClass } from './InductionsPage';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBD';

const groupByPhase = (sessions) => {
  const groups = [];
  sessions.forEach(s => {
    const key = (s.phase || '').trim();
    let g = groups.find(x => x.key === key);
    if (!g) { g = { key, items: [] }; groups.push(g); }
    g.items.push(s);
  });
  return groups;
};

const StarRating = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`}>
        <Star className={`h-6 w-6 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
      </button>
    ))}
  </div>
);

const LIKERT = [
  { v: 5, label: 'Strongly Agree' },
  { v: 4, label: 'Agree' },
  { v: 3, label: 'Neutral' },
  { v: 2, label: 'Disagree' },
  { v: 1, label: 'Strongly Disagree' },
];

const QuestionField = ({ q, value, onChange, idx, scale }) => {
  if (q.type === 'rating') return <StarRating value={value || 0} onChange={onChange} testid={`fb-q-${idx}`} />;
  if (q.type === 'likert') {
    const opts = (scale && scale.length) ? scale.map(sp => ({ v: sp.value, label: sp.label })) : LIKERT;
    return (
      <div className="flex flex-col gap-1.5" data-testid={`fb-q-${idx}`}>
        {opts.map(o => (
          <Button key={o.v} type="button" size="sm" variant={value === o.v ? 'default' : 'outline'} className="justify-start"
            onClick={() => onChange(o.v)} data-testid={`fb-q-${idx}-likert-${o.v}`}>{o.v} – {o.label}</Button>
        ))}
      </div>
    );
  }
  if (q.type === 'text') return <Textarea rows={q.subtype === 'long' ? 4 : 2} value={value || ''} onChange={e => onChange(e.target.value)} data-testid={`fb-q-${idx}`} placeholder="Your answer..." />;
  if (q.type === 'yesno') return (
    <div className="flex gap-2" data-testid={`fb-q-${idx}`}>
      {['Yes', 'No'].map(o => <Button key={o} type="button" size="sm" variant={value === o ? 'default' : 'outline'} onClick={() => onChange(o)} data-testid={`fb-q-${idx}-${o.toLowerCase()}`}>{o}</Button>)}
    </div>
  );
  if (q.type === 'single') return (
    <div className="space-y-1.5" data-testid={`fb-q-${idx}`}>
      {(q.options || []).map(o => <Button key={o} type="button" size="sm" variant={value === o ? 'default' : 'outline'} className="mr-2" onClick={() => onChange(o)}>{o}</Button>)}
    </div>
  );
  if (q.type === 'multi') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
    return (
      <div className="space-y-1.5" data-testid={`fb-q-${idx}`}>
        {(q.options || []).map(o => (
          <label key={o} className="flex items-center gap-2 text-sm"><Checkbox checked={arr.includes(o)} onCheckedChange={() => toggle(o)} /> {o}</label>
        ))}
      </div>
    );
  }
  return null;
};

export default function MyInductionPage() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fb, setFb] = useState(null); // {ind, session}
  const [answers, setAnswers] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/inductions/my`, { headers: getAuthHeader() });
    if (r.ok) setRows(await r.json());
    setLoading(false);
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const openFeedback = (ind, session) => { setAnswers({}); setFb({ ind, session }); };

  const submit = async () => {
    const tpl = fb.session.feedback_template;
    const items = (tpl?.sections?.length ? tpl.sections.flatMap(s => s.items || []) : (tpl?.questions || []));
    for (const q of items) {
      const v = answers[q.id];
      const empty = v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
      if (q.required && empty) { toast.error(`Please answer: ${q.label}`); return; }
    }
    const r = await fetch(`${API}/inductions/${fb.ind.induction_id}/feedback`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: fb.session.session_id, answers }),
    });
    if (r.ok) { toast.success('Thanks for your feedback!'); setFb(null); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  const tpl = fb?.session?.feedback_template;
  const fbSections = tpl?.sections?.length
    ? tpl.sections
    : (tpl?.questions?.length ? [{ id: 'legacy', heading: '', description: '', scale: null, items: tpl.questions }] : []);

  const renderCard = (ind) => (
    <Card key={ind.induction_id}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-lg">{ind.title}</CardTitle>
          {['Confirmed', 'Completed'].includes(ind.status) && <Badge className={indStatusClass(ind.status)}>{ind.status}</Badge>}
        </div>
        {ind.joining_date && <p className="text-sm text-muted-foreground">Joining: {fmtDate(ind.joining_date)}</p>}
      </CardHeader>
      <CardContent className="space-y-6">
        {groupByPhase(ind.sessions || []).map((g, gi) => (
          <div key={gi} className="space-y-3">
            {g.key && (
              <div className="flex items-center gap-3" data-testid={`my-phase-${gi}`}>
                <span className="text-xs font-bold tracking-widest uppercase text-primary">{g.key}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            )}
            {g.items.map((s) => {
              const idx = (ind.sessions || []).indexOf(s);
              const done = (ind.feedback_given || []).includes(s.session_id);
              return (
                <div key={s.session_id} className="border rounded-lg p-4" data-testid={`my-session-${idx}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.department}</p>
                    </div>
                    {s.conduct_status === 'Not Conducted' ? <Badge className="bg-rose-600 text-white hover:bg-rose-600">Not Conducted</Badge>
                      : s.completed ? <Badge className="bg-violet-600 text-white hover:bg-violet-600">Completed</Badge>
                      : <Badge variant="outline">{s.approval_status}</Badge>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground"><User className="h-4 w-4" /> {s.owner_name || 'TBD'}</div>
                    <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4" /> {fmtDate(s.date)}{s.start_time ? ` · ${range12(s.start_time, s.end_time)}` : ''}</div>
                    {s.mode === 'Online'
                      ? <div className="flex items-center gap-2 sm:col-span-2"><Video className="h-4 w-4 text-muted-foreground" /> {s.meeting_url ? <a href={s.meeting_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid={`join-link-${idx}`}>Join Online</a> : 'Link pending'}</div>
                      : <div className="flex items-center gap-2 text-muted-foreground sm:col-span-2"><MapPin className="h-4 w-4" /> {s.location || 'Location TBD'}</div>}
                  </div>
                  <div className="mt-3">
                    {done ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Feedback submitted</span>
                      : s.conduct_status === 'Not Conducted' ? <span className="text-xs text-rose-600">This session was not conducted.</span>
                      : s.feedback_open ? <Button size="sm" variant="outline" onClick={() => openFeedback(ind, s)} data-testid={`feedback-btn-${idx}`}><MessageSquarePlus className="h-4 w-4 mr-1" /> Give Feedback</Button>
                      : <span className="text-xs text-muted-foreground">Feedback opens once the session owner confirms it was conducted.</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const activeRows = rows.filter(r => !r.is_owner_view && !r.joiner_done);
  const completedRows = rows.filter(r => !r.is_owner_view && r.joiner_done);
  const ownerRows = rows.filter(r => r.is_owner_view);

  const renderOwnerCard = (ind) => (
    <Card key={`own-${ind.induction_id}`} data-testid={`owner-induction-${ind.induction_id}`}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-lg">{ind.title}</CardTitle>
          {['Confirmed', 'Completed'].includes(ind.status) && <Badge className={indStatusClass(ind.status)}>{ind.status}</Badge>}
        </div>
        {ind.joining_date && <p className="text-sm text-muted-foreground">Joining: {fmtDate(ind.joining_date)}</p>}
      </CardHeader>
      <CardContent className="space-y-6">
        {groupByPhase(ind.sessions || []).map((g, gi) => (
          <div key={gi} className="space-y-3">
            {g.key && (
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold tracking-widest uppercase text-primary">{g.key}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            )}
            {g.items.map((s) => {
              const idx = (ind.sessions || []).indexOf(s);
              return (
                <div key={s.session_id} className="border rounded-lg p-4" data-testid={`owner-session-${idx}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.department}</p>
                    </div>
                    {s.conduct_status === 'Not Conducted' ? <Badge className="bg-rose-600 text-white hover:bg-rose-600">Not Conducted</Badge>
                      : s.conduct_status === 'Conducted' ? <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">Conducted</Badge>
                      : s.completed ? <Badge className="bg-violet-600 text-white hover:bg-violet-600">Completed</Badge>
                      : <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Accepted</Badge>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4" /> {fmtDate(s.date)}{s.start_time ? ` · ${range12(s.start_time, s.end_time)}` : ''}</div>
                    {s.meeting_url
                      ? <div className="flex items-center gap-2"><Video className="h-4 w-4 text-muted-foreground" /> <a href={s.meeting_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid={`owner-join-link-${idx}`}>Meeting link</a></div>
                      : null}
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1"><User className="h-3.5 w-3.5" /> Joiners ({(s.joiners || []).length})</p>
                    <div className="flex flex-wrap gap-2" data-testid={`owner-joiners-${idx}`}>
                      {(s.joiners || []).map(j => (
                        <Badge key={j.employee_id} variant="secondary" className="py-1">
                          {j.name} · <span className={j.mode === 'Online' ? 'text-blue-600 ml-1' : 'text-slate-500 ml-1'}>{j.mode}</span>{j.mode === 'Offline' && j.location ? ` (${j.location})` : ''}
                        </Badge>
                      ))}
                      {(s.joiners || []).length === 0 && <span className="text-sm text-muted-foreground">No joiners assigned.</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-induction-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><GraduationCap className="h-6 w-6" /> My Induction</h1>
          <p className="text-muted-foreground">Your onboarding schedule, joining links and feedback.</p>
        </div>

        {loading ? <p className="text-muted-foreground">Loading...</p>
          : rows.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="my-induction-empty">
              <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No induction assigned to you right now.</p>
            </CardContent></Card>
          ) : (
            <>
              {ownerRows.length > 0 && (
                <div className="space-y-6" data-testid="owner-leading-section">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold tracking-widest uppercase text-primary">Sessions You're Leading</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  {ownerRows.map(renderOwnerCard)}
                </div>
              )}
              {activeRows.map(renderCard)}
              {completedRows.length > 0 && (
                <div className="space-y-6 pt-2" data-testid="my-induction-completed-section">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Completed</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <div className="space-y-6 opacity-90">
                    {completedRows.map(renderCard)}
                  </div>
                </div>
              )}
            </>
          )}
      </div>

      <Dialog open={!!fb} onOpenChange={(o) => !o && setFb(null)}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
          <DialogHeader><DialogTitle>Feedback — {fb?.session?.name}</DialogTitle></DialogHeader>
          <div className="space-y-8">
            {tpl?.intro_message && <div className="rounded-lg bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="fb-intro">{tpl.intro_message}</div>}
            {fbSections.length === 0 && <p className="text-sm text-muted-foreground">No feedback form configured.</p>}
            {(() => { let gi = -1; return fbSections.map((sec, si) => (
              <div key={sec.id || si} className="space-y-4" data-testid={`fb-section-${si}`}>
                {sec.heading && (
                  <div className="border-b pb-2">
                    <p className="font-semibold text-base">{sec.heading}</p>
                    {sec.description && <p className="text-xs text-muted-foreground mt-0.5">{sec.description}</p>}
                  </div>
                )}
                {(sec.items || []).map(q => { gi += 1; const idx = gi; return (
                  <div key={q.id}>
                    <p className="text-sm mb-1.5">{q.label}{q.required && ' *'}</p>
                    <QuestionField q={q} idx={idx} scale={sec.scale} value={answers[q.id]} onChange={(v) => setAnswers(a => ({ ...a, [q.id]: v }))} />
                  </div>
                ); })}
              </div>
            )); })()}
          </div>
          <DialogFooter><Button onClick={submit} data-testid="fb-submit-btn">Submit Feedback</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
